// Core imports
import { t } from 'i18next';
import React, { Fragment, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

// Component imports
import { Button, Card, Col, Collapse, Form, Input, Popconfirm, Row, Select, Space, Typography } from 'antd';
import { CloseCircleFilled, DownOutlined } from '@ant-design/icons';
import Prompt from '../UI/Prompt';
import FormSectionHeader from '../UI/FormSectionHeader';
import { DeleteIcon } from '../Icons/DeleteIcon';
import TestSetsImportModal from '../Modals/TestSetsImportModal';

// Misc imports
import { useTestSetNavigation } from '../../utils/hooks/useTestSetsNavigation';
import { useCreateTestSetMutation, useFetchTestSetQuery, useUpdateTestSetMutation } from '../../store/testSets/testSetsApi';
import { LANGUAGE_OPTIONS } from '../../constants/main';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { useFetchAutomatedAnswersQuery } from '../../store/automatedAnswers/automatedAnswersApi';
import { isEmptyObject } from '../../utils/is/isEmptyObject';

const transformCasesToGroupedByAA = (cases = []) => {
  let groupedCases = [];

  cases?.forEach(caseItem => {
    const currentCaseIndex = groupedCases?.findIndex(item => item?.intent === caseItem?.intent);

    if(currentCaseIndex === -1) {
      groupedCases?.push({
        intent: caseItem?.intent,
        inputs: [caseItem?.input]
      });
    } else {
      groupedCases[currentCaseIndex]?.inputs.push(caseItem?.input);
    }
  });

  return groupedCases;
};

const transformCasesToKeyValuePair = (cases) => {
  let keyValuePairCases = [];

  cases?.forEach(caseItem => {
    caseItem?.inputs?.forEach(input => {
      keyValuePairCases.push({
        input,
        intent: caseItem?.intent
      });
    });
  });

  return keyValuePairCases;
};

const TestSetEditForm = forwardRef(({
  activeTestSetId = null,
  setIsLoading = () => false,
  isCreateMode = false,
  ...props
}, ref) => {
  const { orgId, tenantId } = useParams();
  const [form] = Form.useForm();

  const { navigateToTestSetsList } = useTestSetNavigation();
  const { currentBotLanguage } = useBotLanguage();

  const [collapsedInputFields, setCollapsedInputFields] = useState([]);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const { data: automatedAnswers } = useFetchAutomatedAnswersQuery({ orgId, tenantId });
  const { currentData: activeTestSet, isFetching: isFetchingTestSet } = useFetchTestSetQuery(
    {
      orgId,
      tenantId,
      testId: activeTestSetId
    }, {
      skip: isCreateMode || !activeTestSetId,
      selectFromResult: ({ currentData }) => {

        if(!currentData) {
          return { currentData: {} };
        }
        const transformedCases = transformCasesToGroupedByAA(currentData?.cases);
        return {
          currentData: {
            ...currentData,
            cases: transformedCases
          }
        };
      }
    });
  const [updateTestSet, { isLoading: isUpdatingTestSet }] = useUpdateTestSetMutation();
  const [createTestSet, { isLoading: isCreatingTestSet }] = useCreateTestSetMutation();

  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isCurrentlyLoading, setIsCurrentlyLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateLoadingState = (loading) => {
    setIsCurrentlyLoading(loading);
    setIsLoading(loading);
  };

  useEffect(() => {
    updateLoadingState(isCreatingTestSet || isUpdatingTestSet || isFetchingTestSet);
  }, [isCreatingTestSet, isUpdatingTestSet, isFetchingTestSet]);

  useImperativeHandle(ref, () => ({
    renderFooter: ({ disabled }) => renderEditFooter(disabled)
  }));

  const handleCsvImport = (data) => {
    const payload = [
      ...transformCasesToKeyValuePair(form.getFieldValue('cases')),
      ...data
    ];

    const transformedPayload = transformCasesToGroupedByAA(payload);

    form.setFieldsValue({ cases: [...transformedPayload] });
    setHasPendingChanges(true);
    setIsImportModalOpen(false);
  };

  const handleCsvRestore = (data) => {
    form.setFieldsValue({ cases: [...transformCasesToGroupedByAA(data)] });
    setHasPendingChanges(true);
    setIsImportModalOpen(false);
  };

  const renderEditFooter = (disabled) => {
    return (
      <Row justify="end" gutter={8}>
        <Col>
          <Button
            size="large"
            onClick={() => {
              setHasPendingChanges(false);
              navigateToTestSetsList();
            }}
            disabled={isCurrentlyLoading}
          >
            {t('Cancel')}
          </Button>
        </Col>

        <Col>
          <Button
            type="primary"
            onClick={() => form.submit()}
            size="large"
            disabled={disabled}
            loading={isCurrentlyLoading}
          >
            {isCreateMode ? t('Create Test Set') : t('Save Changes')}
          </Button>
        </Col>
      </Row>
    );
  };

  const onSubmit = async () => {
    const valuesToSubmit = await form.getFieldsValue(true);

    const transformedValues = {
      ...valuesToSubmit,
      cases: transformCasesToKeyValuePair(valuesToSubmit?.cases)
    };

    let isSaveSuccessful = false;

    if(isCreateMode) {
      isSaveSuccessful = await createTestSet({ orgId, tenantId, data: transformedValues }).unwrap();
    } else {
      isSaveSuccessful = await updateTestSet({ orgId, tenantId, nluTestId: activeTestSetId, data: transformedValues }).unwrap();
    }

    if (isSaveSuccessful) {
      setHasPendingChanges(false);
      if(isCreateMode) {
        navigateToTestSetsList();
      }
    }
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      const values = await form.validateFields();
      await onSubmit(values);
      setHasPendingChanges(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
    setIsSubmitting(false);
  };

  const handleInputFieldCollapse = (index, shouldCollapse = false, shouldUncollapse = false) => {
    setCollapsedInputFields(prev => {
      if(shouldCollapse) {
        return [...prev, index];
      }

      if(shouldUncollapse) {
        return prev.filter(item => item !== index);
      }

      if (!prev.includes(index)) {
        return [...prev, index];
      }

      return prev.filter(item => item !== index);
    });
  };

  return (
    <Fragment>
      <Prompt
        when={hasPendingChanges && !isSubmitting} // Only allow prompt when not submitting because of state sync issue
        onConfirm={handleConfirmSubmit}
        okText={t('Save Changes')}
        cancelText={t('Quit without saving')}
        title={t('Save Changes?')}
        message={t('Your unsaved changes will be lost. Save changes before closing?')}
      />
      {(!isEmptyObject(activeTestSet) || isCreateMode) && (
        <Form
          name="testSetForm"
          layout="vertical"
          initialValues={activeTestSet}
          form={form}
          onFinish={onSubmit}
          onFieldsChange={(fieldData, currentData) => {
            const anyFieldTouched = currentData.some(field => field.touched);
            if(anyFieldTouched) {
              setHasPendingChanges(anyFieldTouched);
            }
          }}
        >
          <Collapse defaultActiveKey={['2']} ghost>
            <Collapse.Panel
              showArrow={false}
              collapsible="icon"
              header={<FormSectionHeader title={t('Test Details')} uppercase collapsible={false} />}
              key="2"
            >
              <Row justify="space-between">
                <Col xs={11}>
                  <Form.Item
                    label="Name"
                    name="name"
                    required
                    rules={[{
                      required: true,
                      message: t('Please enter a name.')
                    }]}
                  >
                    <Input
                      maxLength={20}
                      placeholder="Set test name (max 20 characters)"
                    />
                  </Form.Item>
                  <Form.Item
                    required
                    name="language"
                    label="Language"
                    rules={[{
                      required: true,
                      message: t('Please pick a language.')
                    }]}
                  >
                    <Select
                      placeholder={t('Choose a language')}
                      className="fs-base"
                      // disabled={!canEdit}
                      allowClear="true"
                      showSearch
                      filterOption={(inputValue, option) => option.searchValue?.toLowerCase()?.includes(inputValue?.toLowerCase())}
                      options={
                        LANGUAGE_OPTIONS.sort((a, b) => a.label.localeCompare(b.label)).map(lang => ({
                          label: <Space size={4}>{lang?.icon} {lang.label}</Space>,
                          value: lang.key,
                          searchValue: lang.label
                        }))
                      }
                      getPopupContainer={triggerNode => triggerNode.parentElement}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    label="Description"
                    name="description"
                  >
                    <Input.TextArea
                      placeholder="Set test description"
                      maxLength={260}
                      autoSize={{
                        minRows: 5,
                        maxRows: 5
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Collapse.Panel>
          </Collapse>
          <Collapse defaultActiveKey={['2']} ghost>
            <Collapse.Panel
              showArrow={false}
              collapsible="icon"
              header={<FormSectionHeader title={t('Test Cases')} uppercase collapsible={false} />}
              key="2"
            >
              <Row>
                <Col xs={24}>
                  <Button
                    size="large"
                    className="float-right"
                    onClick={() => setIsImportModalOpen(true)}
                  >
                    {isCreateMode ? t('Import cases') : t('Import / Export Cases')}
                  </Button>
                </Col>
              </Row>
              <Row className="mb-3 mt-3 justify-content-center fw-600">
                <Col xs={10}>
                  {t('User Input')}
                </Col>
                <Col xs={10}>
                  {t('Expected Automated Answer')}
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <Form.Item
                    shouldUpdate
                    className="mb-2 py-2 "
                  >
                    {() => (
                      <Form.List
                        name="cases"
                      >
                        {(fields, { add: addTestCase, remove: removeTestCase }) => (
                          <Fragment>
                            {fields?.length === 0 && addTestCase()}
                            {fields.map((field, testCaseIndex) => (
                              <Card
                                key={field.key}
                                className="bg-layout mb-3 shadow-none"
                              >
                                <Row className="justify-content-evenly">
                                  <Col xs={1}>
                                    <Space direction="vertical" className="d-flex align-items-center mt-1">
                                      <Typography.Text className={'white-space-nowrap bg-layout-gray ant-card-bordered border-radius-base px-2'}>
                                        {form.getFieldValue(['cases', testCaseIndex, 'inputs'])?.length || 0}
                                      </Typography.Text>
                                      <Typography.Text
                                        className={classNames('fs-lg', {
                                          'cursor-not-allowed': form.getFieldValue(['cases', testCaseIndex, 'inputs'])?.length <= 1,
                                          'cursor-pointer': form.getFieldValue(['cases', testCaseIndex, 'inputs'])?.length > 1
                                        })}
                                        disabled={form.getFieldValue(['cases', testCaseIndex, 'inputs'])?.length <= 1}
                                        onClick={() => {
                                          if(form.getFieldValue(['cases', testCaseIndex, 'inputs'])?.length <= 1) {
                                            return;
                                          }
                                          handleInputFieldCollapse(testCaseIndex);
                                        }}
                                      >
                                        <DownOutlined rotate={!collapsedInputFields.includes(testCaseIndex) ? -90 : 0}/>
                                      </Typography.Text>
                                    </Space>
                                  </Col>
                                  <Col
                                    xs={10}
                                  >
                                    <Form.List name={[field.name, 'inputs']}>
                                      {(inputFields, { add: addInput, remove: removeInput }) => (
                                        <Fragment>
                                          <div
                                            style={{ maxHeight: '300px' }}
                                            className="scrollbar-custom overflow-y-auto w-100 scrollbar-custom-nohide"
                                          >
                                            {inputFields?.length === 0 && addInput()}
                                            {inputFields.map((inputField, inputIndex) => (
                                              <Form.Item
                                                key={inputField.key}
                                                {...inputField}
                                                className="mb-1"
                                                name={inputField.name}
                                                rules={[{ required: true, message: 'Please enter a user input.' }]}
                                                hidden={inputIndex !== 0 && !collapsedInputFields.includes(testCaseIndex)}
                                              >
                                                <Input
                                                  placeholder="Enter a user input"
                                                  size="large"
                                                  className="w-100 fs-base input-suffix-on-hover"
                                                  suffix={
                                                    inputFields?.length > 1 ? (
                                                      <CloseCircleFilled
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          e.preventDefault();
                                                          removeInput(inputIndex);
                                                          if(form.getFieldValue(['cases', testCaseIndex, 'inputs'])?.length <= 1) {
                                                            handleInputFieldCollapse(testCaseIndex, false, true);
                                                          }
                                                        }}
                                                      />
                                                    ) : null
                                                  }
                                                />
                                              </Form.Item>
                                            ))}
                                          </div>

                                          <Button
                                            type="dashed"
                                            onClick={() => {
                                              handleInputFieldCollapse(testCaseIndex, true);
                                              addInput();
                                            }}
                                            block
                                          >
                                            {t('Add Input')}
                                          </Button>
                                        </Fragment>
                                      )}
                                    </Form.List>
                                  </Col>
                                  <Col xs={10}>
                                    <Form.Item
                                      {...field}
                                      name={[field.name, 'intent']}
                                      rules={[{ required: true, message: 'Please choose an Automated Answer.' }]}
                                    >
                                      <Select
                                        className="w-100 fs-base"
                                        size="large"
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                          input?.trim() ? option.children?.toLowerCase().includes(input.trim()?.toLowerCase()) : true
                                        }
                                        placeholder={t('Choose an expected Automated Answer')}
                                        getPopupContainer={triggerNode => triggerNode.parentElement}
                                      >
                                        {
                                          automatedAnswers?.items?.map(answer => (
                                            <Select.Option key={answer.id} value={answer.id}>
                                              {answer.name[[currentBotLanguage]]}
                                            </Select.Option>
                                          ))
                                        }
                                      </Select>
                                    </Form.Item>
                                  </Col>
                                  <Col xs={1}>
                                    <Popconfirm
                                      title={t('Delete this test case?')}
                                      okText={t('Yes')}
                                      cancelText={t('No')}
                                      disabled={fields?.length === 1}
                                      placement="topRight"
                                      onConfirm={() => {
                                        removeTestCase(testCaseIndex);
                                        handleInputFieldCollapse(testCaseIndex, false, true);
                                      }}
                                    >
                                      <Button
                                        disabled={fields?.length === 1}
                                        type="text"
                                        className="text-primary"
                                        shape="circle"
                                        icon={<DeleteIcon />}
                                      />
                                    </Popconfirm>
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                            <Button
                              size="large"
                              type="dashed"
                              onClick={() => {
                                addTestCase();
                              }}
                              block
                            >
                              {t('Add Test Case')}
                            </Button>
                          </Fragment>
                        )}
                      </Form.List>
                    )}

                  </Form.Item>
                </Col>
              </Row>
            </Collapse.Panel>
          </Collapse>
        </Form>
      )}
      <TestSetsImportModal
        cases={transformCasesToKeyValuePair(activeTestSet?.cases)}
        onClose={() => setIsImportModalOpen(false)}
        isModalOpen={isImportModalOpen}
        isCreateMode={isCreateMode}
        handleImport={handleCsvImport}
        handleRestore={handleCsvRestore}
      />
    </Fragment>
  );
});

TestSetEditForm.propTypes = {
  activeTestSetId: PropTypes.string,
  setIsLoading: PropTypes.func,
  isCreateMode: PropTypes.bool
};

export default TestSetEditForm;
